import {
  Dir,
  Item,
  MAX_PLAYERS,
  Sickness,
  normalizeCode,
  CODE_LENGTH,
  type ChatMessage,
  type ClientMsg,
  type PlayerView,
  type ServerMsg,
  type Snapshot,
} from "../shared/protocol.js";
import { Sfx } from "./audio.js";
import { Playback, setHtml, setText } from "./playback.js";
import { PLAYER_COLORS, Renderer } from "./render.js";

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const screens = ["loading", "login", "home", "room"] as const;
type ScreenName = (typeof screens)[number];

function show(name: ScreenName) {
  for (const s of screens) $(`screen-${s}`).hidden = s !== name;
}

function toast(text: string) {
  const t = $("toast");
  t.textContent = text;
  t.hidden = false;
  clearTimeout((toast as any).timer);
  (toast as any).timer = setTimeout(() => (t.hidden = true), 2600);
}

function store(key: string, value?: string): string | null {
  try {
    if (value === undefined) return localStorage.getItem(key);
    localStorage.setItem(key, value);
  } catch {}
  return null;
}

const sfx = new Sfx();
const renderer = new Renderer($<HTMLCanvasElement>("canvas"));

// ---------- Connection ----------

class Connection {
  ws: WebSocket | null = null;
  code = "";
  you = "";
  private token = "";
  private closedByUs = false;
  private retries = 0;
  private joined = false;

  constructor(private first: ClientMsg) {
    this.open();
  }

  private open() {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${location.host}/ws`);
    this.ws = ws;
    ws.onopen = () => {
      this.retries = 0;
      const msg: ClientMsg =
        this.joined && this.token ? { t: "join", code: this.code, name: myName(), token: this.token } : this.first;
      ws.send(JSON.stringify(msg));
    };
    ws.onmessage = (ev) => this.onMessage(JSON.parse(ev.data) as ServerMsg);
    ws.onclose = () => {
      if (this.closedByUs || ws !== this.ws) return;
      if (!this.joined) {
        // Never got in: most likely the password cookie is missing or expired.
        void boot();
        return;
      }
      setStatus("Connection lost. Reconnecting…");
      const delay = Math.min(5000, 500 * 2 ** this.retries++);
      setTimeout(() => !this.closedByUs && this.open(), delay);
    };
  }

  private onMessage(msg: ServerMsg) {
    if (msg.t === "joined") {
      this.joined = true;
      this.code = msg.code;
      this.you = msg.you;
      this.token = msg.token;
      try {
        sessionStorage.setItem(`bman:token:${msg.code}`, msg.token);
      } catch {}
      if (location.pathname !== `/r/${msg.code}`) history.replaceState(null, "", `/r/${msg.code}`);
      $("room-code").textContent = msg.code;
      setStatus("");
      show("room");
    } else if (msg.t === "error") {
      if (!this.joined) {
        this.close();
        conn = null;
        history.replaceState(null, "", "/");
        goHome(msg.message);
      } else {
        toast(msg.message);
      }
    } else if (msg.t === "s") {
      onSnapshot(msg);
    } else if (msg.t === "chat") {
      addChat(msg.messages);
    }
  }

  send(msg: ClientMsg) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  close() {
    this.closedByUs = true;
    this.ws?.close();
  }
}

let conn: Connection | null = null;

function myName() {
  return ($<HTMLInputElement>("home-name").value.trim() || "Player").slice(0, 16);
}

function setStatus(text: string) {
  const s = $("room-status");
  s.textContent = text;
  s.hidden = !text;
}

// ---------- Snapshots and interpolation ----------

let snap: Snapshot | null = null; // newest snapshot (UI, sounds, buttons)
const playback = new Playback();
let lastFrame = 0;

function onSnapshot(s: Snapshot) {
  playback.push(s);
  snap = s;
  for (const e of new Set(s.events)) sfx.play(e);
  sfx.setMusic(s.phase === "playing");
  updateUi(s);
}

function frame(now: number) {
  const dt = Math.min(0.1, (now - (lastFrame || now)) / 1000);
  lastFrame = now;
  const f = snap && !$("screen-room").hidden ? playback.frame(dt) : null;
  if (f) renderer.draw(f.s, f.players, f.bombs, conn?.you ?? "", now, snap!.phase === "lobby" ? 0 : f.age);
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

// ---------- UI ----------

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

function updateUi(s: Snapshot) {
  const you = conn?.you ?? "";
  const me = s.players.find((p) => p.id === you);

  // HUD cards, one per seat
  const bySlot = new Map(s.players.map((p) => [p.color, p]));
  setHtml($("hud"), Array.from({ length: MAX_PLAYERS }, (_, slot) => {
    const p = bySlot.get(slot);
    if (!p) return `<div class="pcard empty seat${slot}"><div class="name">Open seat</div><div class="stats">&nbsp;</div></div>`;
    const dead = s.phase !== "lobby" && p.playing && !p.alive;
    const stars = "★".repeat(p.wins) + "☆".repeat(Math.max(0, 3 - p.wins));
    const extras = [
      p.kick ? icon(Item.Kick, "Kick") : "",
      p.throw ? icon(Item.Throw, "Throw glove") : "",
      p.punch ? icon(Item.Punch, "Punch glove") : "",
      p.remote ? icon(Item.Remote, "Remote") : "",
      p.shield > 0 ? icon(Item.Shield, "Shield") : "",
      p.sick !== Sickness.None ? icon(Item.Skull, SICK_NAMES[p.sick] ?? "Sick") : "",
    ].join("");
    const tags = [p.id === you ? "you" : "", p.id === s.hostId ? "host" : "", p.bot ? "CPU" : "", p.connected ? "" : "offline"].filter(Boolean).join(", ");
    return `<div class="pcard seat${slot}${dead ? " dead" : ""}">
      <div class="name">${escapeHtml(p.name)}${tags ? ` <span class="muted">(${tags})</span>` : ""}</div>
      <div class="stats"><span class="wins">${stars}</span> ${icon(Item.Bomb, "Bombs")}${p.bombs} ${icon(Item.Flame, "Flame")}${p.fire} ${icon(Item.Roller, "Speed")}${Math.round(p.speed / 12) / 10}${extras}${dead ? " · out" : ""}</div>
    </div>`;
  }).join(""));

  // Lobby panel
  const inLobby = s.phase === "lobby";
  $("lobby").hidden = !inLobby;
  if (inLobby) {
    const isHost = s.hostId === you;
    setHtml($("lobby-players"), s.players
      .map(
        (p) =>
          `<li><span class="swatch seat${p.color}"></span>${escapeHtml(p.name)}<span class="tag">${[
            p.id === you ? "you" : "",
            p.id === s.hostId ? "host" : "",
            p.bot ? "computer" : "",
            p.connected ? "" : "offline",
          ]
            .filter(Boolean)
            .join(" · ")}</span>${
            isHost && p.bot ? `<button class="remove" data-remove="${escapeHtml(p.id)}" title="Remove">✕</button>` : ""
          }</li>`,
      )
      .join(""));
    $("lobby-add-bot").hidden = !isHost || s.players.length >= MAX_PLAYERS;
    const select = $<HTMLSelectElement>("lobby-level");
    if (select.options.length !== s.levels.length + 1) {
      select.innerHTML =
        `<option value="-1">Random level each round</option>` +
        s.levels.map((name, i) => `<option value="${i}">Level ${escapeHtml(name.replace(/^L0?/, ""))}</option>`).join("");
    }
    if (document.activeElement !== select) select.value = String(s.levelChoice);
    select.disabled = !isHost;
    $("lobby-start").hidden = !isHost;
    setText($("lobby-start"), s.players.length < 2 ? "Start (solo practice)" : "Start match");
    $("lobby-wait").hidden = isHost;
  }

  // Big centered text
  let overlay = "";
  if (s.phase === "countdown") overlay = `${s.message}<br><small>Level ${escapeHtml(s.level.replace(/^L0?/, ""))}</small><br>${s.timer}`;
  else if (s.phase === "roundover" || s.phase === "matchover") overlay = s.message;
  else if (s.phase === "playing" && me && !me.playing) overlay = `<small>You'll join next round</small>`;
  else if (s.phase === "playing" && me && !me.alive) overlay = `<small>You're out. Watching…</small>`;
  setHtml($("overlay"), overlay);
  $("overlay").hidden = !overlay;

  // Only computer players left: anyone watching can fast-forward the rest of the round.
  const fastBtn = $("room-fast");
  fastBtn.hidden = !(s.canFast || s.fast);
  setText(fastBtn, s.fast ? "▶ Normal speed" : "⏩ Speed up");
  fastBtn.classList.toggle("on", s.fast);

  const timer = $("room-timer");
  if (s.phase === "playing") {
    setText(timer, `${Math.floor(s.timer / 60)}:${String(s.timer % 60).padStart(2, "0")}`);
    timer.classList.toggle("danger", s.hurry >= 0);
  } else {
    setText(timer, s.phase === "lobby" ? "" : `Round ${s.round}`);
    timer.classList.remove("danger");
  }
}

const SICK_NAMES = [
  "Sick: controls reversed",
  "Sick: tiny flames",
  "Sick: can't drop bombs",
  "Sick: drops bombs nonstop",
  "Sick: slow",
  "Sick: too fast",
  "Sick: can't stop walking",
  "Sick: long fuse",
  "Sick: short fuse",
  "Sick: invisible",
  "Sick: flameproof but can't bomb",
];

// Small item icon cut from arena_item.png at half size (classes, since the CSP blocks inline styles).
function icon(item: Item, title: string) {
  return `<span class="ico i${item}" title="${title}"></span>`;
}

function goHome(error = "") {
  show("home");
  $("home-error").textContent = error;
  $("home-error").hidden = !error;
  snap = null;
  playback.clear();
}

// ---------- Chat ----------

const chatLog = $("chat-log");
const chatInput = $<HTMLInputElement>("chat-input");
const seenChat = new Set<number>();

function clearChat() {
  seenChat.clear();
  chatLog.innerHTML = `<li class="empty">Chat with everyone in this room.</li>`;
}

function addChat(messages: ChatMessage[]) {
  const nearBottom = chatLog.scrollHeight - chatLog.scrollTop - chatLog.clientHeight < 40;
  for (const m of messages) {
    if (seenChat.has(m.id)) continue; // history is resent after a reconnect
    seenChat.add(m.id);
    chatLog.querySelector(".empty")?.remove();
    const li = document.createElement("li");
    li.title = new Date(m.at).toLocaleTimeString();
    if (m.color < 0) {
      li.className = "notice";
      li.textContent = m.text;
    } else {
      const who = document.createElement("span");
      who.className = m.color === 1 ? "who dark" : "who";
      who.style.setProperty("--c", PLAYER_COLORS[m.color]);
      who.textContent = m.from;
      li.append(who, document.createTextNode(m.text));
    }
    // Keep id order: a join notice can arrive just before the room's history.
    li.dataset.id = String(m.id);
    const later = [...chatLog.children].find((el) => Number((el as HTMLElement).dataset.id) > m.id);
    chatLog.insertBefore(li, later ?? null);
  }
  while (chatLog.children.length > 100) chatLog.firstElementChild!.remove();
  if (nearBottom || messages.length > 1) chatLog.scrollTop = chatLog.scrollHeight;
}

$("chat-form").addEventListener("submit", (e) => {
  e.preventDefault();
  const text = chatInput.value.trim();
  if (text) conn?.send({ t: "chat", text });
  chatInput.value = "";
  chatInput.blur(); // hand the keyboard back to the game
});

chatInput.addEventListener("keydown", (e) => {
  if (e.key === "Escape") chatInput.blur();
});

// Stop moving when you start typing.
chatInput.addEventListener("focus", () => {
  held.length = 0;
  syncDir();
});

function startConnection(first: ClientMsg) {
  sfx.unlock();
  conn?.close();
  snap = null;
  playback.clear();
  clearChat();
  $("room-code").textContent = "-----";
  conn = new Connection(first);
}

function joinCode(code: string) {
  const token = (() => {
    try {
      return sessionStorage.getItem(`bman:token:${code}`) ?? undefined;
    } catch {
      return undefined;
    }
  })();
  startConnection({ t: "join", code, name: myName(), token });
}

$("home-name").addEventListener("input", () => store("bman:name", myName()));

$("home-create").addEventListener("click", () => {
  store("bman:name", myName());
  startConnection({ t: "create", name: myName() });
});

$("join-form").addEventListener("submit", (e) => {
  e.preventDefault();
  const code = normalizeCode($<HTMLInputElement>("home-code").value);
  if (code.length !== CODE_LENGTH) {
    $("home-error").textContent = `Room codes are ${CODE_LENGTH} characters.`;
    $("home-error").hidden = false;
    return;
  }
  store("bman:name", myName());
  history.pushState(null, "", `/r/${code}`);
  joinCode(code);
});

$("lobby-add-bot").addEventListener("click", () => conn?.send({ t: "addBot" }));
$("lobby-players").addEventListener("click", (e) => {
  const id = (e.target as HTMLElement).dataset.remove;
  if (id) conn?.send({ t: "removeBot", id });
});

$<HTMLSelectElement>("lobby-level").addEventListener("change", (e) => {
  conn?.send({ t: "level", level: Number((e.target as HTMLSelectElement).value) });
});

$("lobby-start").addEventListener("click", () => {
  sfx.unlock();
  conn?.send({ t: "start" });
});

$("room-leave").addEventListener("click", () => {
  conn?.send({ t: "leave" });
  conn?.close();
  conn = null;
  history.pushState(null, "", "/");
  goHome();
});

$("room-copy").addEventListener("click", async () => {
  if (!conn?.code) return;
  const link = `${location.origin}/r/${conn.code}`;
  try {
    await navigator.clipboard.writeText(link);
    toast("Invite link copied. Friends also need the server password.");
  } catch {
    window.prompt("Copy this invite link:", link);
  }
});

$("room-fast").addEventListener("click", () => {
  conn?.send({ t: "fast", on: !snap?.fast });
});

$("room-mute").addEventListener("click", () => {
  sfx.unlock();
  $("room-mute").textContent = sfx.toggle() ? "🔇" : "🔊";
});
$("room-mute").textContent = sfx.muted ? "🔇" : "🔊";

$("login-form").addEventListener("submit", async (e) => {
  e.preventDefault();
  const password = $<HTMLInputElement>("login-password").value;
  const res = await fetch("/api/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ password }),
  });
  const body = await res.json().catch(() => ({}));
  if (res.ok && body.ok) {
    $("login-error").hidden = true;
    route();
  } else {
    $("login-error").textContent = body.error ?? "Could not sign in";
    $("login-error").hidden = false;
  }
});

window.addEventListener("popstate", () => {
  conn?.close();
  conn = null;
  route();
});

// ---------- Input ----------

const KEY_DIRS: Record<string, Dir> = {
  ArrowUp: Dir.Up,
  KeyW: Dir.Up,
  ArrowDown: Dir.Down,
  KeyS: Dir.Down,
  ArrowLeft: Dir.Left,
  KeyA: Dir.Left,
  ArrowRight: Dir.Right,
  KeyD: Dir.Right,
};
const A_KEYS = new Set(["Space", "KeyX", "KeyJ"]); // drop a bomb; hold to carry, release to throw
const B_KEYS = new Set(["KeyC", "KeyK", "ShiftLeft", "ShiftRight"]); // stop a kicked bomb, punch, detonate
const held: Dir[] = [];
const aKeys = new Set<string>();
let touchDir = Dir.None;
let touchA = false;
let sentDir = Dir.None;
let sentA = false;

function syncDir() {
  const dir = touchDir || held[held.length - 1] || Dir.None;
  const a = touchA || aKeys.size > 0;
  if (dir !== sentDir || a !== sentA) {
    sentDir = dir;
    sentA = a;
    conn?.send({ t: "input", dir, a });
  }
}

function typing(e: Event) {
  const el = e.target as HTMLElement;
  return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT";
}

window.addEventListener("keydown", (e) => {
  if (typing(e) || $("screen-room").hidden) return;
  if (e.code === "Enter" || e.code === "NumpadEnter" || e.code === "KeyT") {
    e.preventDefault();
    chatInput.focus();
    return;
  }
  const dir = KEY_DIRS[e.code];
  if (dir) {
    e.preventDefault();
    if (!held.includes(dir)) held.push(dir);
    syncDir();
  } else if (A_KEYS.has(e.code)) {
    e.preventDefault();
    aKeys.add(e.code);
    syncDir();
  } else if (B_KEYS.has(e.code)) {
    e.preventDefault();
    if (!e.repeat) conn?.send({ t: "b" });
  }
});

window.addEventListener("keyup", (e) => {
  if (aKeys.delete(e.code)) {
    syncDir();
    return;
  }
  const dir = KEY_DIRS[e.code];
  if (!dir) return;
  const i = held.indexOf(dir);
  if (i >= 0) held.splice(i, 1);
  syncDir();
});

window.addEventListener("blur", () => {
  held.length = 0;
  aKeys.clear();
  touchDir = Dir.None;
  touchA = false;
  syncDir();
});

// Touch controls: a d-pad you can slide your thumb across, plus A (bomb) and B buttons.
const touchCapable = "ontouchstart" in window || navigator.maxTouchPoints > 0;
$("touch").hidden = !touchCapable;
const dpad = document.querySelector<HTMLElement>(".dpad")!;
let dpadPointer: number | null = null;

function dpadAt(x: number, y: number) {
  const el = document.elementFromPoint(x, y) as HTMLElement | null;
  const d = Number(el?.dataset.dir ?? 0) as Dir;
  dpad.querySelectorAll("button").forEach((b) => b.classList.toggle("on", Number(b.dataset.dir) === d && d !== 0));
  touchDir = d;
  syncDir();
}

dpad.addEventListener("pointerdown", (e) => {
  dpadPointer = e.pointerId;
  dpad.setPointerCapture(e.pointerId);
  dpadAt(e.clientX, e.clientY);
});
dpad.addEventListener("pointermove", (e) => e.pointerId === dpadPointer && dpadAt(e.clientX, e.clientY));
const dpadEnd = (e: PointerEvent) => {
  if (e.pointerId !== dpadPointer) return;
  dpadPointer = null;
  dpadAt(-1, -1);
};
dpad.addEventListener("pointerup", dpadEnd);
dpad.addEventListener("pointercancel", dpadEnd);

const touchBomb = $("touch-bomb");
touchBomb.addEventListener("pointerdown", (e) => {
  e.preventDefault();
  sfx.unlock();
  touchBomb.setPointerCapture(e.pointerId);
  touchBomb.classList.add("on");
  touchA = true;
  syncDir();
});
const bombUp = () => {
  touchBomb.classList.remove("on");
  touchA = false;
  syncDir();
};
touchBomb.addEventListener("pointerup", bombUp);
touchBomb.addEventListener("pointercancel", bombUp);

const touchB = $("touch-b");
touchB.addEventListener("pointerdown", (e) => {
  e.preventDefault();
  touchB.classList.add("on");
  conn?.send({ t: "b" });
});
touchB.addEventListener("pointerup", () => touchB.classList.remove("on"));

// ---------- Boot ----------

function route() {
  const name = store("bman:name");
  if (name) $<HTMLInputElement>("home-name").value = name;
  const m = location.pathname.match(/^\/r\/([A-Za-z0-9]+)/);
  const code = m ? normalizeCode(m[1]) : "";
  if (code.length === CODE_LENGTH && name) {
    joinCode(code);
    return;
  }
  goHome();
  if (code) {
    $<HTMLInputElement>("home-code").value = code;
    $("home-name").focus();
    $("home-error").textContent = "Pick a name, then press Join.";
    $("home-error").hidden = false;
  }
}

async function boot() {
  conn?.close();
  conn = null;
  try {
    const me = await (await fetch("/api/me", { cache: "no-store" })).json();
    if (me.gated && !me.authorized) {
      show("login");
      $("login-password").focus();
      return;
    }
  } catch {
    show("loading");
    setTimeout(boot, 2000);
    return;
  }
  route();
}

void boot();
